import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";

interface MovieCardProps {
  movie: Movie;
}

export default function MovieCard({ movie }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(movie.id);

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block bg-background-900 rounded-lg overflow-hidden border border-background-800 hover:border-primary-700 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-800">
        <img
          src={movie.cover}
          alt={`${movie.title}（${movie.year}）中文字幕电影封面`}
          title={`${movie.title} 中文字幕`}
          className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/10 to-transparent" />

        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-background-950 text-xs font-label font-medium whitespace-nowrap">
          {movie.rating.toFixed(1)}分
        </span>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(movie.id);
          }}
          aria-label={favorited ? "取消收藏" : "收藏影片"}
          className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
            favorited
              ? "bg-primary-600 text-background-50"
              : "bg-background-950/70 text-foreground-200 hover:text-accent-400"
          }`}
        >
          <i className={favorited ? "ri-heart-3-fill text-sm" : "ri-heart-3-line text-sm"} />
        </button>

        <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-background-950/80 text-foreground-200 text-[11px] font-label border border-background-700 whitespace-nowrap">
          {movie.version}
        </span>
      </div>

      <div className="p-3">
        <h3 className="font-heading text-foreground-50 font-semibold text-sm md:text-base leading-snug line-clamp-1 group-hover:text-accent-400 transition-colors">
          {movie.title}
        </h3>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-400 font-label">
          <span>{movie.year}</span>
          <span className="w-px h-3 bg-background-700" />
          <span className="line-clamp-1">{movie.genres.join(" · ")}</span>
        </div>
        <p className="mt-1.5 text-xs text-foreground-400 font-label line-clamp-2 leading-relaxed">
          {movie.description}
        </p>
      </div>
    </Link>
  );
}